import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { css } from 'styled-components'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { IconClose } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import SearchField from 'components/Form/SearchField/SearchField'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import StackedAvatar from 'components/UI/StackedAvatar/StackedAvatar'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import HighLightText from 'features/HighLightText/HighLightText'
import { useUserProfile } from 'store'

import styles from './_who-are-you-writing-to-modal.module.scss'

type AudienceSelection =
  | { kind: 'group'; group: { id: string | number; name: string; avatars: string[]; count: number } }
  | { kind: 'users'; users: any[] }
  | { kind: 'none' }

interface Props {
  groups: any[]
  applyFn: (sel: AudienceSelection) => void
  closeFn: () => void
  allUsersData: any
  resetOnCancel?: boolean
}

export const WhoAreYouWritingModal = ({
  groups,
  applyFn,
  closeFn,
  allUsersData,
  resetOnCancel = true,
}: Props) => {
  const [search, setSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [selectedGroup, setSelectedGroup] = useState<{
    id: string | number
    name: string
    avatars: string[]
    count: number
  } | null>(null)
  const unFocusRef = useRef<any>(null)
  const userData = useUserProfile()

  const { t } = useTranslation()
const users = Array.isArray(allUsersData)
  ? allUsersData
  : Array.isArray(allUsersData?.data?.users)
    ? allUsersData.data.users
    : Array.isArray(allUsersData?.users)
      ? allUsersData.users
      : []
  const toggleGroup = (group: {
    id: string | number
    name: string
    avatars: string[]
    count?: number
  }) => {
    if (selectedGroup && selectedGroup.id === group.id) {
      setSelectedGroup(null)
    } else {
      setSelectedGroup({
        id: group.id,
        name: group.name,
        avatars: group.avatars,
        count: typeof group.count === 'number' ? group.count : 0,
      })
    }
    setSelectedUsers([])
  }

  const addSelectedUser = (
    user: Record<string, string | boolean | number | any | any[]>
  ) => {
    setSelectedUsers((prevState: any) => {
      const oldArray = prevState.filter((item: any) => item.id !== user.id)
      if (oldArray.length === prevState.length) {
        return [...oldArray, user]
      }
      return prevState
    })
    if (unFocusRef.current) {
      unFocusRef.current.focus()
    }
    setSearch('')
    setSelectedGroup(null)
  }

  const deleteSelectedUser = (id: number) => {
    setSelectedUsers((prevState: any) => {
      const oldArray = prevState.filter((item: any) => item.id !== id)
      return oldArray
    })
  }
  return (
    <>
      <div className={styles.modal_container}>
        <div ref={unFocusRef} tabIndex={0}></div>
        <div className={styles.modal_title}>Send a Message</div>
        <div
          className={styles.input_container}
          onFocus={() => {
            setSearchOpen(true)
          }}
          onBlur={() => {
            setSearchOpen(false)
          }}
          tabIndex={0}
        >
          <SearchField
            value={search}
            changeFn={(val: string) => {
              setSearch(val)
            }}
            clearFn={() => {
              setSearch('')
            }}
            additionalProps={{
              placeholder:
                userData?.role === 'model' ? t('searchFans') : t('searchCreators'),
            }}
            customClass={`${styles.search_field} ${
              searchOpen && search.trim() !== '' ? styles.search_field_open : ''
            }`}
          />
          <div
            className={`${styles.search_results} ${
              searchOpen && search.trim() !== '' ? styles.search_results_open : ''
            }`}
          >
{users
  .filter(
    (user: any) =>
      user.name?.toLowerCase().trim().includes(search.toLowerCase().trim()) ||
      user.username?.toLowerCase().trim().includes(search.toLowerCase().trim())
  )
  .map((user: any) => (
                <ActionCard
                  key={user.id}
                  text={<HighLightText text={user.name} highlight={search} />}
                  description={
                    <HighLightText text={`@${user.username}`} highlight={search} />
                  }
                  avatar={<AvatarHolder img={user?.avatar?.url || ''} size={'50'} />}
                  customHtml={
                    user.friends === 'Friends' ? (
                      <div className={`${styles.search_status} ${styles.search_status_friend}`}>
                        Friend
                      </div>
                    ) : user.isSubscribed ? (
                      <div className={styles.search_status}>Subscribed</div>
                    ) : (
                      <div></div>
                    )
                  }
                  customStyle={css`
                    margin: 0;
                    box-shadow: none;
                    border-radius: 0;
                    cursor: pointer;

                    &:hover {
                      background: #f4f4f4;
                    }
                  `}
                  clickFn={() => {
                    addSelectedUser(user)
                  }}
                />
              ))}
          </div>
        </div>
        <div className={styles.users_container}>
          {!(selectedUsers.length === 0) &&
            selectedUsers
              .slice()
              .reverse()
              .map((user: any) => (
                <div className={styles.selected_user_container} key={user.id}>
                  <AvatarHolder
                    img={user?.avatar?.url || avatarPlaceholder}
                    size="50"
                  />
                  <div className={styles.selected_user_name}>{user.name}</div>
                  <div
                    className={styles.selected_user_delete}
                    onClick={() => {
                      deleteSelectedUser(user.id)
                    }}
                  >
                    <IconClose color="#0D1444" />
                  </div>
                </div>
              ))}
        </div>
        <div className={styles.separator}></div>
        <div className={styles.groups_conatiner}>
          {!(groups.length === 0) &&
            groups.map((group: any, index: any) => (
              <div key={group.id}>
                <ActionCard
                  text={group.name}
                  description={
                    group.count && group.count > 0
                      ? `${group.count} ${
                          group.name.toLowerCase() === 'fans' ||
                          group.name.toLowerCase() === 'friends'
                            ? group.name
                            : 'Users'
                        }`
                      : 'Empty'
                  }
                  hasRadio
                  toggleActive={selectedGroup && selectedGroup.id === group.id}
                  customHtml={
                    <StackedAvatar
                      // Only pass truthy URLs so users without avatars don't render images,
                      // but they WILL be included in the +N via the `count` prop.
                      avatars={(group.avatars || []).filter(Boolean)}
                      count={group.count}
                    />
                  }
                  clickFn={() => {
                    toggleGroup(group)
                  }}
                />
                {index === 0 && <div className={styles.separator}></div>}
              </div>
            ))}
        </div>
        <div className={styles.users_container_scroll}>
          {userData?.role === 'fan' &&
            users
              .slice()
              .reverse()
              .map((user: any) => (
                <div className={styles.users_list} key={user.id}>
                  <div
                    className={styles.user_avatar}
                    onClick={() => {
                      addSelectedUser(user) // you already have this helper
                    }}
                  >
                    <AvatarHolder
                      img={user?.avatar?.url || avatarPlaceholder}
                      size="50"
                    />
                  </div>
                  <div className={styles.selected_user_name}>{user.name}</div>
                </div>
              ))}
        </div>
      </div>
      <div className={styles.buttons_container}>
        <Button
          text={t('cancel')}
          color="grey"
          font="mont-14-normal"
          height="4"
          width="10"
          clickFn={() => {
            closeFn()
            if (resetOnCancel) {
              setSearch('')
              setSelectedGroup(null)
              setSelectedUsers([])
            }
          }}
        />
        <Button
          text={t('apply')}
          color="black"
          font="mont-14-normal"
          height="4"
          width="14"
          clickFn={() => {
            if (selectedGroup) {
              applyFn({ kind: 'group', group: selectedGroup })
            } else if (selectedUsers.length > 0) {
              applyFn({ kind: 'users', users: selectedUsers })
            } else {
              applyFn({ kind: 'none' })
            }
          }}
        />
      </div>
    </>
  )
}
